import { useEffect, useMemo, useState } from "react";
import { Instagram, Trash2, ExternalLink, Plus, ChevronLeft, ChevronRight, Users } from "lucide-react";
import { toast } from "sonner";
import { api, usd, errMsg, fileUrl } from "@/lib/api";
import { PageTitle } from "./ResourcePage";

const STATUS = [["activa", "Activa"], ["pausada", "Pausada"], ["terminada", "Terminada"]];
const STATUS_COLOR = { activa: "bg-emerald-50 border-emerald-300 text-emerald-700 font-semibold", pausada: "bg-amber-50 border-amber-300 text-amber-700 font-semibold", terminada: "bg-red-50 border-red-300 text-red-700 font-semibold" };
const ORDER = { activa: 0, pausada: 1, terminada: 2 };
const inp = "h-9 min-w-0 rounded-lg border border-slate-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF3333]/30";
const thisMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
const shift = (m, n) => { const [y, mo] = m.split("-").map(Number); const d = new Date(y, mo - 1 + n, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
const monthName = (m) => { const [y, mo] = m.split("-").map(Number); return new Date(y, mo - 1, 1).toLocaleDateString("es-DO", { month: "long", year: "numeric" }); };
const rowMonth = (r) => r.month || (r.start_date || "").slice(0, 7);

const Cell = ({ row, k, type = "text", onSave, w = "w-24" }) => {
  const [v, setV] = useState(row[k] ?? "");
  const cur = row[k];
  useEffect(() => { setV(cur ?? ""); }, [cur]);
  return <input type={type} value={v} onChange={(e) => setV(e.target.value)} onBlur={() => String(v) !== String(row[k] ?? "") && onSave({ [k]: v })}
    className={`${inp} ${w}`} data-testid={`igad-${k}-${row.id}`} />;
};

const Sel = ({ row, k, opts, onSave, w = "w-28", cls = "" }) => (
  <select value={row[k] || ""} onChange={(e) => onSave({ [k]: e.target.value })} className={`${inp} ${w} ${cls}`} data-testid={`igad-${k}-${row.id}`}>
    {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

const Stat = ({ l, v, tid, children }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={tid}>
    <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{l}</p>{children || <p className="font-mono-num text-2xl mt-2">{v}</p>}
  </div>
);

function Clock() {
  const [t, setT] = useState(new Date());
  useEffect(() => { const i = setInterval(() => setT(new Date()), 1000); return () => clearInterval(i); }, []);
  return <p className="text-white/80 text-sm sm:text-base capitalize" data-testid="igad-clock">{t.toLocaleDateString("es-DO", { weekday: "long", day: "numeric", month: "long", year: "numeric" })} · {t.toLocaleTimeString("es-DO", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</p>;
}

function MonthHeader({ month, setMonth }) {
  return (
    <div className="rounded-3xl bg-[#121316] text-white p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="igad-month-header">
      <div className="space-y-1">
        <p className="text-xs uppercase tracking-[0.25em] text-[#FF3333] font-semibold">{month === thisMonth() ? "Mes en curso" : "Mes seleccionado"}</p>
        <h2 className="text-4xl sm:text-5xl font-extrabold capitalize" data-testid="igad-month-name">{monthName(month)}</h2>
        <Clock />
      </div>
      <div className="flex items-center gap-2">
        <button onClick={() => setMonth(shift(month, -1))} className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center" data-testid="igad-prev-month" aria-label="Mes anterior"><ChevronLeft /></button>
        {month !== thisMonth() && <button onClick={() => setMonth(thisMonth())} className="h-11 px-4 rounded-full bg-white/10 hover:bg-white/20 text-sm font-semibold" data-testid="igad-today">Mes actual</button>}
        <button onClick={() => setMonth(shift(month, 1))} className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center" data-testid="igad-next-month" aria-label="Mes siguiente"><ChevronRight /></button>
      </div>
    </div>
  );
}

function Potential({ month }) {
  const [doc, setDoc] = useState(null);
  const [v, setV] = useState("");
  useEffect(() => {
    let live = true;
    api.get("/admin/c/ig_months", { params: { month } }).then((r) => { if (live) { setDoc(r.data[0] || null); setV(r.data[0]?.potential_clients ?? ""); } }).catch(() => {});
    return () => { live = false; };
  }, [month]);
  const save = async () => {
    const n = Number(v) || 0;
    if (n === Number(doc?.potential_clients || 0) && doc) return;
    try {
      const r = doc ? await api.put(`/admin/c/ig_months/${doc.id}`, { potential_clients: n }) : await api.post("/admin/c/ig_months", { month, potential_clients: n });
      setDoc(r.data); toast.success("Clientes potenciales guardados");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Stat l="Clientes potenciales" tid="igad-sum-potential">
      <div className="flex items-center gap-2 mt-2"><Users size={20} className="text-[#FF3333]" />
        <input type="number" min="0" value={v} onChange={(e) => setV(e.target.value)} onBlur={save} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} placeholder="0"
          className="font-mono-num text-2xl w-full bg-slate-50 rounded-lg px-2 py-1 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#FF3333]/30" data-testid="igad-potential-input" /></div>
    </Stat>
  );
}

function AddLink({ onAdded }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/instagram/ad-from-link", { url });
      toast.success(r.data.property_code ? `Promoción agregada para ${r.data.property_code}` : "Promoción agregada. Elige la propiedad en la tabla.");
      setUrl(""); onAdded();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <form onSubmit={add} className="flex flex-col sm:flex-row gap-3 bg-white rounded-2xl border border-slate-200 p-4" data-testid="igad-add-form">
      <div className="flex items-center gap-2 flex-1"><Instagram className="text-[#FF3333] shrink-0" size={20} />
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Pega el enlace de Estadísticas o de la publicación de Instagram" className={`${inp} w-full h-11 text-base`} data-testid="igad-link-input" /></div>
      <button disabled={busy} className="h-11 rounded-full bg-[#FF3333] hover:bg-[#E62E2E] text-white px-6 font-semibold inline-flex items-center gap-2 justify-center disabled:opacity-60" data-testid="igad-add-btn"><Plus size={16} />{busy ? "Leyendo..." : "Agregar promoción"}</button>
    </form>
  );
}

function ByProperty({ rows }) {
  const groups = useMemo(() => {
    const g = {};
    rows.forEach((r) => {
      const k = r.property_code || "Sin propiedad";
      g[k] = g[k] || { code: k, title: r.property_title, owner: r.owner_name, n: 0, days: 0, total: 0, leads: 0 };
      Object.assign(g[k], { n: g[k].n + 1, days: g[k].days + (r.calc?.days || 0), total: g[k].total + (r.calc?.total || 0), leads: g[k].leads + (r.leads || 0) });
    });
    return Object.values(g);
  }, [rows]);
  if (!groups.length) return null;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto" data-testid="igad-by-property">
      <h3 className="font-semibold px-5 pt-4">Gasto por propiedad en el mes</h3>
      <table className="w-full text-sm mt-2"><thead className="bg-slate-50 text-xs uppercase text-slate-500 text-left"><tr>{["Propiedad", "Dueño o Realtor", "Promociones", "Días", "Gastado", "Leads"].map((h) => <th key={h} className="px-4 py-3 whitespace-nowrap">{h}</th>)}</tr></thead>
        <tbody>{groups.map((g) => <tr key={g.code} className="border-t" data-testid={`igad-group-${g.code}`}><td className="px-4 py-3"><b>{g.code}</b> <span className="text-slate-500">{g.title}</span></td><td className="px-4 py-3">{g.owner || "—"}</td><td className="px-4 py-3">{g.n}</td><td className="px-4 py-3">{g.days}</td>
          <td className="px-4 py-3 font-mono-num">{usd(g.total)}</td><td className="px-4 py-3">{g.leads}</td></tr>)}</tbody></table>
    </div>
  );
}

const HEAD = ["#", "Propiedad", "Dueño o Realtor", "Status", "Inicio", "Días", "Diario (US$)", "Total (US$)", "Leads", ""];

function Row({ r, n, props, save, del }) {
  const s = (patch) => save(r.id, patch);
  const pick = (code) => { const p = props.find((x) => x.code === code); s({ property_code: code, property_title: p?.title || "", photo: p?.photos?.[0]?.url || "" }); };
  return (
    <tr className="border-t align-middle" data-testid={`igad-row-${r.id}`}>
      <td className="pl-3 pr-1 py-2 font-bold text-slate-400" data-testid={`igad-num-${r.id}`}>{n}</td>
      <td className="px-2 py-2"><div className="flex items-center gap-2 min-w-[200px]">
        {r.photo ? <img src={fileUrl(r.photo)} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" /> : <div className="w-12 h-12 rounded-lg bg-slate-100 shrink-0" />}
        <div className="min-w-0 flex-1"><Sel row={r} k="property_code" opts={[["", "Elegir propiedad"], ...props.map((p) => [p.code, `${p.code} · ${p.title || ""}`])]} onSave={(x) => pick(x.property_code)} w="w-40" />
          {r.property_title && <p className="text-xs text-slate-600 mt-1 truncate max-w-[160px]" title={r.property_title}>{r.property_title}</p>}
          <a href={r.post_url} target="_blank" rel="noreferrer" className="text-xs text-[#FF3333] inline-flex items-center gap-1 mt-0.5">Ver post <ExternalLink size={11} /></a></div></div></td>
      <td className="px-1.5 py-2"><Cell row={r} k="owner_name" onSave={s} w="w-28" /></td>
      <td className="px-1.5 py-2"><Sel row={r} k="status" opts={STATUS} onSave={s} w="w-[7.5rem]" cls={STATUS_COLOR[r.status] || ""} /></td>
      <td className="px-1.5 py-2"><Cell row={r} k="start_date" type="date" onSave={s} w="w-32" /></td>
      <td className="px-1.5 py-2"><Cell row={r} k="days" type="number" onSave={(x) => s({ ...x, total_manual: false })} w="w-14" /></td>
      <td className="px-1.5 py-2"><Cell row={r} k="daily_budget" type="number" onSave={(x) => s({ ...x, total_manual: false })} w="w-20" /></td>
      <td className="px-1.5 py-2"><Cell row={r} k="spend" type="number" onSave={(x) => s({ ...x, total_manual: true })} w="w-20" /></td>
      <td className="px-1.5 py-2"><Cell row={r} k="leads" type="number" onSave={s} w="w-16" /></td>
      <td className="px-1.5 py-2"><button onClick={() => del(r.id)} className="p-2 rounded-full hover:bg-red-50 text-slate-400 hover:text-red-600" data-testid={`igad-delete-${r.id}`}><Trash2 size={16} /></button></td>
    </tr>
  );
}

export default function IgAds() {
  const [all, setAll] = useState([]);
  const [props, setProps] = useState([]);
  const [month, setMonth] = useState(thisMonth());
  const load = () => api.get("/admin/c/ig_ads", { params: { sort: "-start_date" } }).then((r) => setAll(r.data));
  useEffect(() => {
    load();
    api.get("/admin/c/properties", { params: { sort: "sort_order" } }).then((r) => setProps(r.data));
  }, []);
  const rows = all.filter((r) => rowMonth(r) === month).map((r) => (r.days == null ? { ...r, days: r.calc?.days ?? 0 } : r))
    .sort((a, b) => (ORDER[a.status] ?? 3) - (ORDER[b.status] ?? 3) || (b.start_date || "").localeCompare(a.start_date || ""));
  const save = async (id, patch) => {
    try { const r = await api.put(`/admin/c/ig_ads/${id}`, patch); setAll((x) => x.map((y) => (y.id === id ? r.data : y))); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("¿Eliminar esta promoción?")) return;
    try { await api.delete(`/admin/c/ig_ads/${id}`); setAll((x) => x.filter((y) => y.id !== id)); } catch (e) { toast.error(errMsg(e)); }
  };
  const total = rows.reduce((a, r) => a + (r.calc?.total || 0), 0);
  const days = rows.reduce((a, r) => a + (r.calc?.days || 0), 0);
  const leads = rows.reduce((a, r) => a + (r.leads || 0), 0);
  const nProps = new Set(rows.filter((r) => r.property_code).map((r) => r.property_code)).size;
  const nActive = rows.filter((r) => r.status === "activa").length;
  return (
    <div className="space-y-6" data-testid="ads-page">
      <PageTitle title="Publicidad en Instagram" subtitle="Pega el enlace de cada promoción, escribe los días y el pago diario; el total se calcula solo (también puedes escribirlo tú). Las activas salen primero y luego por fecha." />
      <MonthHeader month={month} setMonth={setMonth} />
      <AddLink onAdded={() => { load(); setMonth(thisMonth()); }} />
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <Potential month={month} />
        <Stat l="Gastado en el mes" v={usd(total)} tid="igad-sum-total" />
        <Stat l="Leads del mes" v={leads} tid="igad-sum-leads" />
        <Stat l="Días de publicidad" v={days} tid="igad-sum-days" />
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto" data-testid="igad-table">
        <div className="flex flex-wrap gap-2 px-4 pt-4 text-sm" data-testid="igad-counts">
          <span className="rounded-full bg-slate-100 px-3 py-1"><b>{rows.length}</b> promociones</span>
          <span className="rounded-full bg-slate-100 px-3 py-1"><b>{nProps}</b> propiedades</span>
          <span className="rounded-full bg-emerald-50 text-emerald-700 px-3 py-1"><b>{nActive}</b> activas ahora</span>
        </div>
        <table className="w-full text-sm mt-3"><thead className="bg-slate-50 text-xs uppercase text-slate-500 text-left"><tr>{HEAD.map((h, i) => <th key={i} className="px-2 py-3 whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>{rows.map((r, i) => <Row key={r.id} r={r} n={i + 1} props={props} save={save} del={del} />)}
            {!rows.length && <tr><td colSpan={HEAD.length} className="text-center text-slate-400 py-10" data-testid="igad-empty">No hay promociones en {monthName(month)}.</td></tr>}</tbody></table>
      </div>
      <ByProperty rows={rows} />
    </div>
  );
}
